import React, { useEffect, useState, useCallback, useRef } from 'react';
import { NoWebshopState } from './_webshopNotice';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControlLabel,
    IconButton,
    InputAdornment,
    MenuItem,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LanguageIcon from '@mui/icons-material/Language';
import StorageIcon from '@mui/icons-material/Storage';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import EmailIcon from '@mui/icons-material/Email';
import PaymentsIcon from '@mui/icons-material/Payments';
import MapIcon from '@mui/icons-material/Map';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Colors from '../../library/Colors';
import FloatingSaveButton from '../../components/FloatingSaveButton';
import SftGlobalData from '../../library/SftGlobalData';

const WebshopConnectionsScreen = (props) => {
    useEffect(() => {
        if (props.changeScreen) props.changeScreen('webshop');
    }, []);

    const isDark = props.theme === 'dark';
    const cardBg = isDark ? '#2d333b' : '#ffffff';
    const borderColor = isDark ? '#3a3f47' : '#e4e7eb';
    const textColor = isDark ? Colors.dark_text : Colors.light_text;
    const mutedText = isDark ? '#b0b8c1' : '#768390';

    // URL — stored on `display_name` in `lic_clients_webshop`. Empty until a
    // webshop is picked.
    const liveUrl = props.selectedWebshop?.display_name || '';

    // DB credentials (host/port/name kept server-side; whatever comes back from
    // the decrypted blob is preserved and re-sent on save so we don't lose
    // fields the form doesn't expose).
    const [dbUsername, setDbUsername] = useState('');
    const [dbPassword, setDbPassword] = useState('');
    const [dbHost, setDbHost] = useState('');
    const [dbPort, setDbPort] = useState('');
    const [dbName, setDbName] = useState('');
    const [showDbPassword, setShowDbPassword] = useState(false);

    // SSH connection — a second credential set bundled with the DB credentials.
    // Both are always needed (the SSH credentials may be used in parallel for
    // tunneling, file ops, …). Stored on the same encrypted DB blob so they
    // share the credential's lifecycle.
    const [sshHost,         setSshHost]         = useState('');
    const [sshPort,         setSshPort]         = useState(22);
    const [sshUsername,     setSshUsername]     = useState('');
    const [sshAuthMethod,   setSshAuthMethod]   = useState('password');     // 'password' | 'key'
    const [sshPassword,     setSshPassword]     = useState('');
    const [sshPrivateKey,   setSshPrivateKey]   = useState('');
    const [showSshPassword, setShowSshPassword] = useState(false);

    // SFTP credentials
    const [sftpHost, setSftpHost] = useState('');
    const [sftpPort, setSftpPort] = useState(22);
    const [sftpUsername, setSftpUsername] = useState('');
    const [sftpPassword, setSftpPassword] = useState('');
    const [sftpDirectory, setSftpDirectory] = useState('/');
    const [showSftpPassword, setShowSftpPassword] = useState(false);

    // Track load/save state + the original loaded blobs so we can tell which
    // section has unsaved changes (and so we can preserve unknown keys that
    // might have been written by another tool).
    const [credLoading, setCredLoading] = useState(false);
    const [credSaving, setCredSaving] = useState(false);
    const [credError, setCredError] = useState(null);
    const originalDbRef = useRef(null);    // last-loaded decrypted DB blob
    const originalSftpRef = useRef(null);  // last-loaded decrypted SFTP blob
    const [dbDirty, setDbDirty] = useState(false);
    const [sftpDirty, setSftpDirty] = useState(false);

    // ── Settings-table-backed sections (API / SMTP / MultiSafePay) ──────────
    // These come from the webshop's `setting` table via the mini-api proxy
    // (GET /admin/settings on load, PATCH /admin/settings/{key} on save).
    // Each maps 1:1 to a setting row; originalSettingsRef holds the loaded
    // values so the save handler can compute the diff and PATCH only changes.
    const originalSettingsRef = useRef(null);
    const [apiDirty, setApiDirty] = useState(false);
    const [smtpDirty, setSmtpDirty] = useState(false);
    const [mspDirty, setMspDirty] = useState(false);

    // API credentials → API_BASEURL, API_TOKEN
    const [apiBaseUrl, setApiBaseUrl] = useState('');
    const [apiToken, setApiToken] = useState('');
    const [showApiToken, setShowApiToken] = useState(false);

    // SMTP credentials → MAIL_SMTP_*, MAIL_FROM_*, MAIL_TO_BCC
    const [smtpHost, setSmtpHost] = useState('');
    const [smtpPort, setSmtpPort] = useState(587);
    const [smtpUsername, setSmtpUsername] = useState('');
    const [smtpPassword, setSmtpPassword] = useState('');
    const [smtpSecurity, setSmtpSecurity] = useState('tls');
    const [smtpAuth, setSmtpAuth] = useState(true);
    const [smtpSenderName, setSmtpSenderName] = useState('');
    const [smtpSenderEmail, setSmtpSenderEmail] = useState('');
    const [smtpBcc, setSmtpBcc] = useState('');
    const [showSmtpPassword, setShowSmtpPassword] = useState(false);

    // MultiSafePay → MSP_API_TOKEN, MSP_API_TOKEN_TEST,
    // MSP_IS_PRODUCTION, MSP_SEND_REMINDER, MSP_VOID_TIMEFRAME
    // Google Maps API key — used by the storefront to render store-locator
    // and contact-page maps. Stored as a single setting; no test endpoint
    // (Google's quota response is sufficient signal in the live shop).
    const [gmapsApiKey, setGmapsApiKey] = useState('');
    const [gmapsDirty,  setGmapsDirty]  = useState(false);
    const originalGmapsKeyRef = useRef('');

    const [mspApiToken, setMspApiToken] = useState('');
    const [mspApiTestToken, setMspApiTestToken] = useState('');
    const [mspLiveMode, setMspLiveMode] = useState(false);
    const [mspEnableReminders, setMspEnableReminders] = useState(true);
    const [mspCompletionDays, setMspCompletionDays] = useState(2);
    const [showMspToken, setShowMspToken] = useState(false);
    const [showMspTestToken, setShowMspTestToken] = useState(false);

    // Accordion expansion state — support multiple open at once
    const [expanded, setExpanded] = useState({
        url: false,
        db: false,
        sftp: false,
        api: false,
        smtp: false,
        msp: false,
        gmaps: false,
    });
    const toggle = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

    // ── Load decrypted credentials + settings whenever the webshop changes ──
    const webshopId = props.selectedWebshop?.id;
    useEffect(() => {
        // Reset form whenever the webshop changes; bail out if we have no
        // webshop or no API key yet.
        setDbUsername('');
        setDbPassword('');
        setDbHost('');
        setDbPort('');
        setDbName('');
        setSshHost('');
        setSshPort(22);
        setSshUsername('');
        setSshAuthMethod('password');
        setSshPassword('');
        setSshPrivateKey('');
        setSftpHost('');
        setSftpPort(22);
        setSftpUsername('');
        setSftpPassword('');
        setSftpDirectory('/');
        setApiBaseUrl('');
        setApiToken('');
        setSmtpHost('');
        setSmtpPort(587);
        setSmtpUsername('');
        setSmtpPassword('');
        setSmtpSecurity('tls');
        setSmtpAuth(true);
        setSmtpSenderName('');
        setSmtpSenderEmail('');
        setSmtpBcc('');
        setMspApiToken('');
        setMspApiTestToken('');
        setMspLiveMode(false);
        setMspEnableReminders(true);
        setMspCompletionDays(2);
        setGmapsApiKey('');
        originalGmapsKeyRef.current = '';
        originalDbRef.current = null;
        originalSftpRef.current = null;
        originalSettingsRef.current = null;
        setDbDirty(false);
        setSftpDirty(false);
        setApiDirty(false);
        setSmtpDirty(false);
        setMspDirty(false);
        setGmapsDirty(false);
        setCredError(null);

        if (!webshopId || !props.api) return;

        let cancelled = false;
        (async () => {
            setCredLoading(true);
            try {
                // Fan out both calls in parallel — credentials sit on the auth
                // backend, settings are proxied through to the webshop's
                // mini-api (`GET /admin/settings`).
                const [credRes, settingsRes] = await Promise.all([
                    SftGlobalData.post('webshop&action=getCredentials', {
                        key:        props.api,
                        webshop_id: webshopId,
                    }),
                    SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     'GET',
                        path:       '/admin/settings',
                    }),
                ]);
                if (cancelled) return;

                // ---- Credentials (encrypted blobs from auth backend) -------
                if (!credRes || !credRes.success) {
                    setCredError(credRes?.message || 'Failed to load credentials');
                } else {
                    const sftp = credRes.data?.sftp || null;
                    const db   = credRes.data?.db   || null;
                    if (db) {
                        originalDbRef.current = db;
                        setDbUsername(db.username || db.user || '');
                        setDbPassword(db.password || db.pass || '');
                        setDbHost(db.host || '');
                        setDbPort(db.port != null ? String(db.port) : '');
                        setDbName(db.name || db.database || db.dbname || '');
                        // SSH connection — flat ssh* keys on the same blob.
                        setSshHost(db.sshHost || '');
                        setSshPort(db.sshPort != null ? Number(db.sshPort) : 22);
                        setSshUsername(db.sshUsername || db.sshUser || '');
                        setSshAuthMethod(db.sshAuthMethod === 'key' ? 'key' : 'password');
                        setSshPassword(db.sshPassword || '');
                        setSshPrivateKey(db.sshPrivateKey || '');
                    }
                    if (sftp) {
                        originalSftpRef.current = sftp;
                        setSftpHost(sftp.host || '');
                        setSftpPort(sftp.port != null ? Number(sftp.port) : 22);
                        setSftpUsername(sftp.username || sftp.user || '');
                        setSftpPassword(sftp.password || sftp.pass || '');
                        setSftpDirectory(sftp.directory || sftp.dir || '/');
                    }
                }

                // ---- Settings (webshop DB via mini-api proxy) --------------
                if (settingsRes && settingsRes.success && settingsRes.data) {
                    const s = settingsRes.data;
                    originalSettingsRef.current = s;
                    setApiBaseUrl(s.API_BASEURL || '');
                    setApiToken(s.API_TOKEN || '');
                    setSmtpHost(s.MAIL_SMTP_HOST || '');
                    setSmtpPort(s.MAIL_SMTP_PORT != null && s.MAIL_SMTP_PORT !== '' ? Number(s.MAIL_SMTP_PORT) : 587);
                    setSmtpUsername(s.MAIL_SMTP_USERNAME || '');
                    setSmtpPassword(s.MAIL_SMTP_PASSWORD || '');
                    setSmtpSecurity((s.MAIL_SMTP_SECURE || 'tls').toString().toLowerCase());
                    // MAIL_SMTP_AUTH isn't in the mini-api's bool-coercion list,
                    // so it comes back as raw string '0'/'1' — normalize.
                    setSmtpAuth(s.MAIL_SMTP_AUTH === true || s.MAIL_SMTP_AUTH === 1 || s.MAIL_SMTP_AUTH === '1');
                    setSmtpSenderName(s.MAIL_FROM_NAME || '');
                    setSmtpSenderEmail(s.MAIL_FROM_EMAIL || '');
                    setSmtpBcc(s.MAIL_TO_BCC || '');
                    setMspApiToken(s.MSP_API_TOKEN || '');
                    setMspApiTestToken(s.MSP_API_TOKEN_TEST || '');
                    // MSP_IS_PRODUCTION / MSP_SEND_REMINDER are bool-coerced by
                    // the mini-api already; MSP_VOID_TIMEFRAME comes as int.
                    setMspLiveMode(!!s.MSP_IS_PRODUCTION);
                    setMspEnableReminders(!!s.MSP_SEND_REMINDER);
                    setMspCompletionDays(s.MSP_VOID_TIMEFRAME != null ? Number(s.MSP_VOID_TIMEFRAME) : 2);
                    setGmapsApiKey(s.GOOGLE_MAPS_TOKEN || '');
                    originalGmapsKeyRef.current = s.GOOGLE_MAPS_TOKEN || '';
                } else if (settingsRes && !settingsRes.success) {
                    // Don't blow away a successful credentials load — surface
                    // the settings failure separately so the user knows which
                    // half is broken.
                    setCredError((prev) =>
                        prev ? prev : ('Failed to load settings: ' + (settingsRes.message || 'unknown error'))
                    );
                }
            } catch (e) {
                if (!cancelled) setCredError(e?.message || 'Failed to load connections');
            } finally {
                if (!cancelled) setCredLoading(false);
            }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [webshopId, props.api]);

    // Wrap each setter so any field edit flips the section's dirty flag. Keeps
    // the per-section "● unsaved" indicator + save-button enable state honest.
    const editDb   = (setter) => (val) => { setter(val); setDbDirty(true); };
    const editSftp = (setter) => (val) => { setter(val); setSftpDirty(true); };
    const editApi  = (setter) => (val) => { setter(val); setApiDirty(true); };
    const editSmtp = (setter) => (val) => { setter(val); setSmtpDirty(true); };
    const editMsp  = (setter) => (val) => { setter(val); setMspDirty(true); };

    // Helper: PATCH a single setting via the proxy. Returns the parsed result.
    const patchSetting = useCallback((attribute, value) => {
        return SftGlobalData.post('webshop&action=proxy', {
            key:        props.api,
            webshop_id: webshopId,
            sftUserId:  props.login?.sft_user_id,
            user:       props.login?.login,
            method:     'PATCH',
            path:       '/admin/settings/' + encodeURIComponent(attribute),
            body:       { value },
        }).then(r => ({ kind: 'setting', attribute, value, r }));
    }, [props.api, props.login, webshopId]);

    const handleSave = useCallback(async () => {
        if (!webshopId || !props.api) {
            if (props.notify) props.notify('No webshop selected', 4000, 'top-right', props.theme, 1);
            return;
        }
        if (!dbDirty && !sftpDirty && !apiDirty && !smtpDirty && !mspDirty) {
            if (props.notify) props.notify('No changes to save', 3000, 'top-right', props.theme, 3);
            return;
        }
        setCredSaving(true);
        setCredError(null);
        try {
            const calls = [];

            // ---- DB / SFTP (encrypted blobs on auth backend) ---------------
            if (dbDirty) {
                // Merge form values onto whatever else was in the original blob
                // so unknown keys (e.g. host/port if set elsewhere) survive.
                const dbBlob = {
                    ...(originalDbRef.current || {}),
                    username: dbUsername,
                    password: dbPassword,
                    host:     dbHost,
                    port:     dbPort === '' ? null : Number(dbPort),
                    name:     dbName,
                    // SSH credentials — bundled with the DB blob. Only the
                    // password OR the private key is kept depending on the
                    // chosen auth method, to avoid leaving stale data behind.
                    sshHost:       sshHost,
                    sshPort:       Number(sshPort) || 22,
                    sshUsername:   sshUsername,
                    sshAuthMethod: sshAuthMethod === 'key' ? 'key' : 'password',
                    sshPassword:   sshAuthMethod === 'password' ? sshPassword   : '',
                    sshPrivateKey: sshAuthMethod === 'key'      ? sshPrivateKey : '',
                };
                calls.push(
                    SftGlobalData.post('webshop&action=setDb', {
                        key:        props.api,
                        webshop_id: webshopId,
                        db:         dbBlob,
                    }).then(r => ({ kind: 'db', r, blob: dbBlob }))
                );
            }
            if (sftpDirty) {
                const sftpBlob = {
                    ...(originalSftpRef.current || {}),
                    host:      sftpHost,
                    port:      Number(sftpPort) || 22,
                    username:  sftpUsername,
                    password:  sftpPassword,
                    directory: sftpDirectory,
                };
                calls.push(
                    SftGlobalData.post('webshop&action=setSftp', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftp:       sftpBlob,
                    }).then(r => ({ kind: 'sftp', r, blob: sftpBlob }))
                );
            }

            // ---- Settings sections (mini-api `setting` table) --------------
            // Build the full desired-state map for whatever sections are dirty,
            // then diff against the originally-loaded values and PATCH only
            // what actually changed. The PATCH endpoint is one-key-at-a-time.
            const desired = {};
            if (apiDirty) {
                desired.API_BASEURL = apiBaseUrl;
                desired.API_TOKEN   = apiToken;
            }
            if (smtpDirty) {
                desired.MAIL_SMTP_HOST     = smtpHost;
                desired.MAIL_SMTP_PORT     = String(Number(smtpPort) || 0);
                desired.MAIL_SMTP_USERNAME = smtpUsername;
                desired.MAIL_SMTP_PASSWORD = smtpPassword;
                desired.MAIL_SMTP_SECURE   = smtpSecurity;
                desired.MAIL_SMTP_AUTH     = smtpAuth ? '1' : '0';
                desired.MAIL_FROM_NAME     = smtpSenderName;
                desired.MAIL_FROM_EMAIL    = smtpSenderEmail;
                desired.MAIL_TO_BCC        = smtpBcc;
            }
            if (mspDirty) {
                desired.MSP_API_TOKEN      = mspApiToken;
                desired.MSP_API_TOKEN_TEST = mspApiTestToken;
                desired.MSP_IS_PRODUCTION  = mspLiveMode ? '1' : '0';
                desired.MSP_SEND_REMINDER  = mspEnableReminders ? '1' : '0';
                desired.MSP_VOID_TIMEFRAME = String(Number(mspCompletionDays) || 0);
            }
            if (gmapsDirty) {
                desired.GOOGLE_MAPS_TOKEN = gmapsApiKey;
            }

            const original = originalSettingsRef.current || {};
            const changed = {};
            for (const [k, v] of Object.entries(desired)) {
                // Booleans land in `original` as JS booleans (mini-api coerces),
                // ints as numbers. Stringify both sides for an honest compare.
                const oldVal = original[k];
                const oldStr = oldVal === true ? '1' : oldVal === false ? '0'
                              : (oldVal == null ? '' : String(oldVal));
                if (oldStr !== String(v)) {
                    changed[k] = v;
                }
            }
            for (const [k, v] of Object.entries(changed)) {
                calls.push(patchSetting(k, v));
            }

            if (calls.length === 0) {
                if (props.notify) props.notify('No changes to save', 3000, 'top-right', props.theme, 3);
                setApiDirty(false); setSmtpDirty(false); setMspDirty(false); setGmapsDirty(false);
                return;
            }

            const results = await Promise.all(calls);
            const failed = results.filter(x => !x.r || !x.r.success);
            if (failed.length) {
                const msg = failed.map(f =>
                    `${f.kind}${f.attribute ? '/' + f.attribute : ''}: ${f.r?.message || 'failed'}`
                ).join(' · ');
                setCredError(msg);
                if (props.notify) props.notify('Save failed: ' + msg, 6000, 'top-right', props.theme, 1);
                return;
            }

            // Success — fold the just-saved values back into the "original"
            // refs so dirty chips clear and a no-op resave is a true no-op.
            for (const x of results) {
                if (x.kind === 'db')      { originalDbRef.current   = x.blob; setDbDirty(false); }
                if (x.kind === 'sftp')    { originalSftpRef.current = x.blob; setSftpDirty(false); }
                if (x.kind === 'setting') {
                    if (originalSettingsRef.current) {
                        originalSettingsRef.current[x.attribute] = x.value;
                    }
                }
            }
            if (apiDirty)   setApiDirty(false);
            if (smtpDirty)  setSmtpDirty(false);
            if (mspDirty)   setMspDirty(false);
            if (gmapsDirty) {
                originalGmapsKeyRef.current = gmapsApiKey;
                setGmapsDirty(false);
            }

            if (props.notify) props.notify('Connections saved', 4000, 'top-right', props.theme, 2);
        } catch (e) {
            setCredError(e?.message || 'Save failed');
            if (props.notify) props.notify('Save failed: ' + (e?.message || ''), 6000, 'top-right', props.theme, 1);
        } finally {
            setCredSaving(false);
        }
    }, [webshopId, props.api, props.notify, props.theme, patchSetting,
        dbDirty, sftpDirty, apiDirty, smtpDirty, mspDirty, gmapsDirty,
        dbUsername, dbPassword, dbHost, dbPort, dbName,
        sftpHost, sftpPort, sftpUsername, sftpPassword, sftpDirectory,
        apiBaseUrl, apiToken,
        smtpHost, smtpPort, smtpUsername, smtpPassword, smtpSecurity, smtpAuth,
        smtpSenderName, smtpSenderEmail, smtpBcc,
        mspApiToken, mspApiTestToken, mspLiveMode, mspEnableReminders, mspCompletionDays,
        gmapsApiKey]);

    const renderPasswordField = ({ label, value, onChange, visible, onToggle, placeholder }) => (
        <TextField
            label={label}
            size="small"
            fullWidth
            type={visible ? 'text' : 'password'}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton size="small" onClick={onToggle} edge="end" tabIndex={-1}>
                            {visible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                    </InputAdornment>
                ),
            }}
        />
    );

    const accordionSx = {
        backgroundColor: cardBg,
        border: `1px solid ${borderColor}`,
        borderRadius: 2,
        boxShadow: 'none',
        '&::before': { display: 'none' },
        '&.Mui-expanded': { m: 0 },
    };

    const summarySx = {
        px: 2.5,
        py: 0.5,
        minHeight: 80,
        '&.Mui-expanded': { minHeight: 80 },
        '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            gap: 1.5,
            my: 1.5,
        },
        '& .MuiAccordionSummary-content.Mui-expanded': {
            my: 1.5,
        },
    };

    const descriptionSx = {
        color: mutedText,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    };

    const iconBoxSx = {
        width: 36,
        height: 36,
        borderRadius: 1.25,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: isDark ? 'rgba(25,118,210,0.12)' : 'rgba(25,118,210,0.08)',
        color: Colors.sft_blue,
        flexShrink: 0,
        '& svg': { fontSize: 20 },
    };

    // No webshop linked → nothing to configure; show an explicit empty-state
    // instead of an editable credentials form bound to nothing.
    if (!webshopId) {
        return (
            <div className={(props.screenWidth < 600 ? 'not_expanded' : 'expanded') + ' container_content _80perc ' + props.theme + ' container_content_' + props.theme}>
                <Box sx={{ border: `1px solid ${borderColor}`, borderRadius: 2, backgroundColor: cardBg, p: 3 }}>
                    <NoWebshopState t={props.t} />
                </Box>
            </div>
        );
    }

    return (
        <div className={(props.screenWidth < 600 ? 'not_expanded' : 'expanded') + ' container_content _80perc ' + props.theme + ' container_content_' + props.theme}>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" sx={{ color: textColor, fontWeight: 500, mb: 0.5 }}>
                    Webshop connections
                </Typography>
                <Typography variant="body2" sx={{ color: mutedText }}>
                    Credentials and tokens used by the webshop integrations. SFTP and DB credentials are AES-GCM encrypted on the auth backend; the form below decrypts them for editing and re-encrypts on save.
                </Typography>
                {credError && (
                    <Alert severity="error" sx={{ mt: 2 }} onClose={() => setCredError(null)}>
                        {credError}
                    </Alert>
                )}
            </Box>

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
                alignItems: 'start',
            }}>


            {/* DB Credentials */}
            <Accordion expanded={expanded.db} onChange={() => toggle('db')} disableGutters sx={accordionSx}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
                    <Box sx={iconBoxSx}><StorageIcon /></Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: textColor }}>
                            Database credentials
                        </Typography>
                        <Typography variant="caption" sx={descriptionSx}>Connection params for the webshop's MySQL database. AES-GCM encrypted on the auth backend.</Typography>
                    </Box>
                    {credLoading && <CircularProgress size={16} sx={{ mr: 1 }} />}
                    {dbDirty && <Chip label="Unsaved" size="small" color="warning" sx={{ mr: 1 }} />}
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2.5, pb: 2.5 }}>
                    <Stack spacing={2}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: 2 }}>
                            <TextField
                                label="Host"
                                size="small"
                                fullWidth
                                value={dbHost}
                                onChange={(e) => editDb(setDbHost)(e.target.value)}
                                placeholder="db.example.com"
                                disabled={!webshopId || credLoading}
                            />
                            <TextField
                                label="Port"
                                type="number"
                                size="small"
                                fullWidth
                                value={dbPort}
                                onChange={(e) => editDb(setDbPort)(e.target.value)}
                                placeholder="3306"
                                disabled={!webshopId || credLoading}
                            />
                        </Box>
                        <TextField
                            label="Database name"
                            size="small"
                            fullWidth
                            value={dbName}
                            onChange={(e) => editDb(setDbName)(e.target.value)}
                            disabled={!webshopId || credLoading}
                        />
                        <TextField
                            label="Username"
                            size="small"
                            fullWidth
                            value={dbUsername}
                            onChange={(e) => editDb(setDbUsername)(e.target.value)}
                            disabled={!webshopId || credLoading}
                        />
                        {renderPasswordField({
                            label: 'Password',
                            value: dbPassword,
                            onChange: (e) => editDb(setDbPassword)(e.target.value),
                            visible: showDbPassword,
                            onToggle: () => setShowDbPassword(v => !v),
                        })}

                        {/* ── SSH connection ──────────────────────────────
                            A second credential set bundled with the DB blob.
                            Always shown — both the database and the SSH
                            connection are needed in parallel. */}
                        <Box sx={{ mt: 1, pt: 2, borderTop: `1px dashed ${borderColor}` }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: textColor, mb: 0.25 }}>
                                SSH connection
                            </Typography>
                            <Typography variant="caption" sx={{ color: mutedText, display: 'block', lineHeight: 1.4, mb: 1.5 }}>
                                SSH access used alongside the database connection.
                            </Typography>
                            <Stack spacing={2}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: 2 }}>
                                    <TextField
                                        label="SSH host"
                                        size="small"
                                        fullWidth
                                        value={sshHost}
                                        onChange={(e) => editDb(setSshHost)(e.target.value)}
                                        placeholder="ssh.example.com"
                                        disabled={!webshopId || credLoading}
                                    />
                                    <TextField
                                        label="SSH port"
                                        type="number"
                                        size="small"
                                        fullWidth
                                        value={sshPort}
                                        onChange={(e) => editDb(setSshPort)(e.target.value)}
                                        placeholder="22"
                                        disabled={!webshopId || credLoading}
                                    />
                                </Box>
                                <TextField
                                    label="SSH username"
                                    size="small"
                                    fullWidth
                                    value={sshUsername}
                                    onChange={(e) => editDb(setSshUsername)(e.target.value)}
                                    disabled={!webshopId || credLoading}
                                />
                                <TextField
                                    select
                                    label="Authentication method"
                                    size="small"
                                    fullWidth
                                    value={sshAuthMethod}
                                    onChange={(e) => editDb(setSshAuthMethod)(e.target.value)}
                                    disabled={!webshopId || credLoading}
                                >
                                    <MenuItem value="password">Password</MenuItem>
                                    <MenuItem value="key">Private key</MenuItem>
                                </TextField>
                                {sshAuthMethod === 'password' ? (
                                    renderPasswordField({
                                        label: 'SSH password',
                                        value: sshPassword,
                                        onChange: (e) => editDb(setSshPassword)(e.target.value),
                                        visible: showSshPassword,
                                        onToggle: () => setShowSshPassword(v => !v),
                                    })
                                ) : (
                                    <TextField
                                        label="Private key"
                                        size="small"
                                        fullWidth
                                        multiline
                                        minRows={4}
                                        value={sshPrivateKey}
                                        onChange={(e) => editDb(setSshPrivateKey)(e.target.value)}
                                        placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
                                        helperText="Paste the contents of the private key file. Stored encrypted alongside the database credentials."
                                        disabled={!webshopId || credLoading}
                                        sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.78rem' } }}
                                    />
                                )}
                            </Stack>
                        </Box>
                    </Stack>
                </AccordionDetails>
            </Accordion>

            {/* API Credentials → API_BASEURL, API_TOKEN */}
            <Accordion expanded={expanded.api} onChange={() => toggle('api')} disableGutters sx={accordionSx}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
                    <Box sx={iconBoxSx}><VpnKeyIcon /></Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: textColor }}>
                            API credentials
                        </Typography>
                        <Typography variant="caption" sx={descriptionSx}>Base URL and token used by the webshop to call the legacy storefront API.</Typography>
                    </Box>
                    {credLoading && <CircularProgress size={16} sx={{ mr: 1 }} />}
                    {apiDirty && <Chip label="Unsaved" size="small" color="warning" sx={{ mr: 1 }} />}
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2.5, pb: 2.5 }}>
                    <Stack spacing={2}>
                        <TextField
                            label="Base URL"
                            size="small"
                            fullWidth
                            value={apiBaseUrl}
                            onChange={(e) => editApi(setApiBaseUrl)(e.target.value)}
                            placeholder="https://api.example.com"
                            disabled={!webshopId || credLoading}
                        />
                        {renderPasswordField({
                            label: 'API token',
                            value: apiToken,
                            onChange: (e) => editApi(setApiToken)(e.target.value),
                            visible: showApiToken,
                            onToggle: () => setShowApiToken(v => !v),
                        })}
                    </Stack>
                </AccordionDetails>
            </Accordion>

            {/* SFTP Credentials */}
            <Accordion expanded={expanded.sftp} onChange={() => toggle('sftp')} disableGutters sx={accordionSx}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
                    <Box sx={iconBoxSx}><FolderSharedIcon /></Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: textColor }}>
                            SFTP credentials
                        </Typography>
                        <Typography variant="caption" sx={descriptionSx}>Required to push files to the webserver. AES-GCM encrypted on the auth backend.</Typography>
                    </Box>
                    {credLoading && <CircularProgress size={16} sx={{ mr: 1 }} />}
                    {sftpDirty && <Chip label="Unsaved" size="small" color="warning" sx={{ mr: 1 }} />}
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2.5, pb: 2.5 }}>
                    <Stack spacing={2}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: 2 }}>
                            <TextField
                                label="Host"
                                size="small"
                                fullWidth
                                value={sftpHost}
                                onChange={(e) => editSftp(setSftpHost)(e.target.value)}
                                placeholder="sftp.example.com"
                                disabled={!webshopId || credLoading}
                            />
                            <TextField
                                label="Port"
                                type="number"
                                size="small"
                                fullWidth
                                value={sftpPort}
                                onChange={(e) => editSftp(setSftpPort)(parseInt(e.target.value) || 0)}
                                disabled={!webshopId || credLoading}
                            />
                        </Box>
                        <TextField
                            label="Username"
                            size="small"
                            fullWidth
                            value={sftpUsername}
                            onChange={(e) => editSftp(setSftpUsername)(e.target.value)}
                            disabled={!webshopId || credLoading}
                        />
                        {renderPasswordField({
                            label: 'Password',
                            value: sftpPassword,
                            onChange: (e) => editSftp(setSftpPassword)(e.target.value),
                            visible: showSftpPassword,
                            onToggle: () => setShowSftpPassword(v => !v),
                        })}
                        <TextField
                            label="Remote directory"
                            size="small"
                            fullWidth
                            value={sftpDirectory}
                            onChange={(e) => editSftp(setSftpDirectory)(e.target.value)}
                            placeholder="/var/www/webshop/public"
                            helperText="Absolute path where webshop files will be uploaded."
                            disabled={!webshopId || credLoading}
                        />
                    </Stack>
                </AccordionDetails>
            </Accordion>

            {/* SMTP Credentials → MAIL_SMTP_*, MAIL_FROM_*, MAIL_TO_BCC */}
            <Accordion expanded={expanded.smtp} onChange={() => toggle('smtp')} disableGutters sx={accordionSx}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
                    <Box sx={iconBoxSx}><EmailIcon /></Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: textColor }}>
                            SMTP credentials
                        </Typography>
                        <Typography variant="caption" sx={descriptionSx}>Outgoing mail server used for order confirmations and reminders.</Typography>
                    </Box>
                    {credLoading && <CircularProgress size={16} sx={{ mr: 1 }} />}
                    {smtpDirty && <Chip label="Unsaved" size="small" color="warning" sx={{ mr: 1 }} />}
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2.5, pb: 2.5 }}>
                    <Stack spacing={2}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '3fr 1fr 1fr auto', gap: 2, alignItems: 'center' }}>
                            <TextField
                                label="Host"
                                size="small"
                                fullWidth
                                value={smtpHost}
                                onChange={(e) => editSmtp(setSmtpHost)(e.target.value)}
                                placeholder="smtp.example.com"
                                disabled={!webshopId || credLoading}
                            />
                            <TextField
                                label="Port"
                                type="number"
                                size="small"
                                fullWidth
                                value={smtpPort}
                                onChange={(e) => editSmtp(setSmtpPort)(parseInt(e.target.value) || 0)}
                                disabled={!webshopId || credLoading}
                            />
                            <TextField
                                select
                                label="Security"
                                size="small"
                                fullWidth
                                value={smtpSecurity}
                                onChange={(e) => editSmtp(setSmtpSecurity)(e.target.value)}
                                disabled={!webshopId || credLoading}
                            >
                                <MenuItem value="none">None</MenuItem>
                                <MenuItem value="tls">TLS / STARTTLS</MenuItem>
                                <MenuItem value="ssl">SSL</MenuItem>
                            </TextField>
                            <FormControlLabel
                                sx={{ ml: 0, mr: 0 }}
                                control={
                                    <Switch color="success"
                                        checked={smtpAuth}
                                        onChange={(e) => editSmtp(setSmtpAuth)(e.target.checked)}
                                        disabled={!webshopId || credLoading}
                                        size="small"
                                    />
                                }
                                label={
                                    <Typography variant="body2" sx={{ color: textColor, whiteSpace: 'nowrap' }}>
                                        Auth
                                    </Typography>
                                }
                            />
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            <TextField
                                label="Username"
                                size="small"
                                fullWidth
                                value={smtpUsername}
                                onChange={(e) => editSmtp(setSmtpUsername)(e.target.value)}
                                disabled={!webshopId || credLoading || !smtpAuth}
                            />
                            {renderPasswordField({
                                label: 'Password',
                                value: smtpPassword,
                                onChange: (e) => editSmtp(setSmtpPassword)(e.target.value),
                                visible: showSmtpPassword,
                                onToggle: () => setShowSmtpPassword(v => !v),
                            })}
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            <TextField
                                label="Sender name"
                                size="small"
                                fullWidth
                                value={smtpSenderName}
                                onChange={(e) => editSmtp(setSmtpSenderName)(e.target.value)}
                                placeholder="Acme Shop"
                                disabled={!webshopId || credLoading}
                            />
                            <TextField
                                label="Sender email"
                                size="small"
                                type="email"
                                fullWidth
                                value={smtpSenderEmail}
                                onChange={(e) => editSmtp(setSmtpSenderEmail)(e.target.value)}
                                placeholder="noreply@example.com"
                                disabled={!webshopId || credLoading}
                            />
                        </Box>
                        <TextField
                            label="BCC recipients"
                            size="small"
                            fullWidth
                            value={smtpBcc}
                            onChange={(e) => editSmtp(setSmtpBcc)(e.target.value)}
                            placeholder="ops@example.com, archive@example.com"
                            helperText="Comma-separated. Receives a blind copy of every outgoing mail."
                            disabled={!webshopId || credLoading}
                        />
                    </Stack>
                </AccordionDetails>
            </Accordion>

            {/* MultiSafePay → MSP_API_TOKEN, MSP_API_TOKEN_TEST,
                MSP_IS_PRODUCTION, MSP_SEND_REMINDER, MSP_VOID_TIMEFRAME */}
            <Accordion expanded={expanded.msp} onChange={() => toggle('msp')} disableGutters sx={accordionSx}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
                    <Box sx={iconBoxSx}><PaymentsIcon /></Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: textColor }}>
                            MultiSafePay
                        </Typography>
                        <Typography variant="caption" sx={descriptionSx}>Payment provider — live and test API tokens, reminder settings.</Typography>
                    </Box>
                    {credLoading && <CircularProgress size={16} sx={{ mr: 1 }} />}
                    {mspDirty && <Chip label="Unsaved" size="small" color="warning" sx={{ mr: 1 }} />}
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2.5, pb: 2.5 }}>
                    <Stack spacing={2}>
                        {renderPasswordField({
                            label: 'API token (live)',
                            value: mspApiToken,
                            onChange: (e) => editMsp(setMspApiToken)(e.target.value),
                            visible: showMspToken,
                            onToggle: () => setShowMspToken(v => !v),
                        })}
                        {renderPasswordField({
                            label: 'API token (test)',
                            value: mspApiTestToken,
                            onChange: (e) => editMsp(setMspApiTestToken)(e.target.value),
                            visible: showMspTestToken,
                            onToggle: () => setShowMspTestToken(v => !v),
                        })}
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                borderRadius: 1.5,
                                borderColor: borderColor,
                                backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                            }}
                        >
                            <Stack spacing={1.5}>
                                <FormControlLabel
                                    control={
                                        <Switch color="success"
                                            checked={mspLiveMode}
                                            onChange={(e) => editMsp(setMspLiveMode)(e.target.checked)}
                                            disabled={!webshopId || credLoading}
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 500, color: textColor }}>
                                                Live (production) mode
                                            </Typography>
                                            <Typography variant="caption" sx={descriptionSx}>When off, transactions go through the test token.</Typography>
                                        </Box>
                                    }
                                />
                                <FormControlLabel
                                    control={
                                        <Switch color="success"
                                            checked={mspEnableReminders}
                                            onChange={(e) => editMsp(setMspEnableReminders)(e.target.checked)}
                                            disabled={!webshopId || credLoading}
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 500, color: textColor }}>
                                                Enable reminders
                                            </Typography>
                                            <Typography variant="caption" sx={descriptionSx}>Automatically email customers who haven't completed payment.</Typography>
                                        </Box>
                                    }
                                />
                            </Stack>
                        </Paper>
                        <TextField
                            label="Completion window"
                            type="number"
                            size="small"
                            value={mspCompletionDays}
                            onChange={(e) => editMsp(setMspCompletionDays)(parseInt(e.target.value) || 0)}
                            InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
                            helperText="How long the customer has to complete the transaction before it expires. Default is 2 days."
                            sx={{ maxWidth: 260 }}
                            disabled={!webshopId || credLoading}
                        />
                    </Stack>
                </AccordionDetails>
            </Accordion>

            {/* Google Maps API key */}
            <Accordion expanded={expanded.gmaps} onChange={() => toggle('gmaps')} disableGutters sx={accordionSx}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
                    <Box sx={iconBoxSx}><MapIcon /></Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: textColor }}>
                            Google Maps
                        </Typography>
                        <Typography variant="caption" sx={descriptionSx}>
                            API key used to render store-locator and contact maps on the storefront.
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2.5, pb: 2.5 }}>
                    <Stack spacing={1.5}>
                        <TextField
                            label="API key"
                            size="small"
                            fullWidth
                            value={gmapsApiKey}
                            onChange={(e) => { setGmapsApiKey(e.target.value); setGmapsDirty(true); }}
                            placeholder="AIzaSy..."
                            helperText="Generate one in the Google Cloud Console — enable the Maps JavaScript API and restrict the key to your shop's domain for safety."
                            disabled={!webshopId || credLoading}
                        />
                    </Stack>
                </AccordionDetails>
            </Accordion>

            {/* URL (read-only) */}
            <Accordion expanded={expanded.url} onChange={() => toggle('url')} disableGutters sx={accordionSx}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
                    <Box sx={iconBoxSx}><LanguageIcon /></Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: textColor }}>
                            Live URL
                        </Typography>
                        <Typography variant="caption" sx={descriptionSx}>Current public URL of the webshop (read-only).</Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2.5, pb: 2.5 }}>
                    <TextField
                        label="Live URL"
                        size="small"
                        fullWidth
                        value={liveUrl}
                        disabled
                        helperText="Change this via the 'Update webshop URL' action on the Actions page."
                    />
                </AccordionDetails>
            </Accordion>

            </Box>

            <FloatingSaveButton
                onClick={handleSave}
                disabled={!webshopId || credLoading || credSaving ||
                    (!dbDirty && !sftpDirty && !apiDirty && !smtpDirty && !mspDirty && !gmapsDirty)}
            />
        </div>
    );
};

export default WebshopConnectionsScreen;
